import { matchPath, type DataRouteMatch, type UIMatch } from 'react-router-dom'
import { PAGE_CONTEXT_BY_PATH } from './pageContextRegistry'

/**
 * Either match shape this module accepts: the public `useMatches()` result,
 * or the router state's own matches, which carry the `route` object the
 * public shape strips.
 */
export type RouteMatchLike = UIMatch | DataRouteMatch

function routeOf(match: RouteMatchLike): { index?: boolean; path?: string } | undefined {
  return (match as Partial<DataRouteMatch>).route
}

/**
 * Resolve the absolute route path pattern for the current router match.
 *
 * React Router's public `useMatches()` shape exposes `id`, `pathname`, `params`,
 * `data`, and `handle`; it does not expose the internal `route` object in all
 * builds. When route metadata is available we reconstruct the pattern from it.
 * Otherwise, fall back to matching the concrete pathname against the declared
 * page-context registry.
 */
export function resolveRoutePattern(matches: readonly RouteMatchLike[], pathname: string): string {
  const routePattern = resolveFromRouteObjects(matches)
  if (routePattern !== undefined) return routePattern

  return resolveFromPathname(pathname)
}

function resolveFromRouteObjects(matches: readonly RouteMatchLike[]): string | undefined {
  let pattern = ''
  for (const match of matches) {
    const route = routeOf(match)
    if (route === undefined) return undefined
    if (route.index === true) {
      if (pattern === '') pattern = '/'
      continue
    }
    const segment = route.path
    if (segment === undefined) continue
    pattern = joinPattern(pattern, segment)
  }
  return pattern === '' ? '/' : pattern
}

function resolveFromPathname(pathname: string): string {
  const normalizedPathname = pathname === '' ? '/' : pathname
  const candidates = Object.keys(PAGE_CONTEXT_BY_PATH).sort(comparePatternSpecificity)
  for (const candidate of candidates) {
    if (matchPath({ path: candidate, end: true }, normalizedPathname) !== null) {
      return candidate
    }
  }
  return '*'
}

function comparePatternSpecificity(a: string, b: string): number {
  const aScore = scorePattern(a)
  const bScore = scorePattern(b)
  return bScore - aScore
}

function scorePattern(pattern: string): number {
  if (pattern === '*') return -1
  const segments = pattern.split('/').filter(Boolean)
  let score = segments.length * 10
  for (const segment of segments) {
    if (segment === '*') score -= 100
    else if (segment.startsWith(':')) score += 1
    else score += 5
  }
  return score
}

function joinPattern(parent: string, segment: string): string {
  if (segment.startsWith('/')) return segment
  if (segment === '*') {
    return parent === '' || parent === '/' ? '*' : `${parent}/*`
  }
  if (parent === '' || parent === '/') return `/${segment}`
  return `${parent}/${segment}`
}
